/**
 * ReportVariantRunModal — execute & render a saved report variant's chart.
 * Supports both single-form (legacy: rows=[{key,value,count}]) and multi-form
 * (rows=[{dim_0, dim_1?, metric_0, metric_1?...}]) variants. Bar/line/pie/pivot/kpi.
 * Multi-form charts support click-to-drill on a bar/cell to see contributing records.
 */
import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { ArrowClockwise, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

const COLORS = ["#002FA7", "#F4B400", "#0F9D58", "#DB4437", "#9C27B0", "#00BCD4", "#FF6F00"];

export function ReportVariantRunModal({ open, onOpenChange, variant }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [drillRows, setDrillRows] = useState(null);
    const [drillLabel, setDrillLabel] = useState("");

    const reload = () => {
        if (!variant) return;
        setLoading(true);
        api.get(`/report-variants/${variant.id}/run`)
            .then((r) => setData(r.data))
            .catch((e) => toast.error(extractError(e)))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (!open || !variant) { setData(null); setDrillRows(null); return; }
        reload();
        // eslint-disable-next-line
    }, [open, variant?.id]);

    if (!variant) return null;
    const isMulti = variant.kind === "multi";
    const chart = variant.chart_type || "bar";
    const rows = data?.rows || [];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="report-run-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center justify-between">
                        <span>{variant.name}</span>
                        <Button size="sm" variant="outline" className="rounded-sm" onClick={reload} data-testid="report-run-refresh"><ArrowClockwise size={14} className="mr-1" /> Refresh</Button>
                    </DialogTitle>
                    <DialogDescription className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs">{variant.form_name || variant.form_id}</span>
                        <Badge variant="outline" className="rounded-sm font-mono text-[9px]">{isMulti ? "multi-form" : variant.metric_op}</Badge>
                        {!isMulti && (
                            <Badge variant="outline" className="rounded-sm font-mono text-[9px]">
                                by {variant.group_by === "__created_day__" ? "day" : variant.group_by}
                            </Badge>
                        )}
                        {variant.description && <span className="text-xs text-muted-foreground">· {variant.description}</span>}
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="text-sm text-muted-foreground py-12 text-center">Running report…</div>
                ) : rows.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-12 text-center" data-testid="report-run-empty">No data for this report.</div>
                ) : drillRows ? (
                    <DrillView rows={drillRows} label={drillLabel} onBack={() => setDrillRows(null)} variant={variant} />
                ) : isMulti ? (
                    <MultiRender data={data} variant={variant} onDrill={async (entry, label) => {
                        try {
                            setDrillLabel(label);
                            const dims = {};
                            (variant.multi_group_by || []).forEach((_d, i) => {
                                if (entry[`dim_${i}`] != null) dims[`dim_${i}`] = entry[`dim_${i}`];
                            });
                            const { data: dd } = await api.post(`/report-variants/${variant.id}/drill`, { dim_values: dims });
                            setDrillRows(dd.items || []);
                        } catch (e) { toast.error(extractError(e)); }
                    }} />
                ) : (
                    <SingleRender data={data} chart={chart} />
                )}
            </DialogContent>
        </Dialog>
    );
}

// ---- Single-form render (legacy) ----
function SingleRender({ data, chart }) {
    const rows = data?.rows || [];
    return (
        <div data-testid="report-run-chart">
            {data?.total_value != null && (
                <div className="mb-3 text-xs text-muted-foreground font-mono">Total: {data.total_value.toLocaleString()}</div>
            )}
            <ResponsiveContainer width="100%" height={360}>
                {chart === "bar" ? (
                    <BarChart data={rows}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="key" tick={{ fontSize: 11 }} interval={0} angle={-25} textAnchor="end" height={70} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="value" fill={COLORS[0]} />
                    </BarChart>
                ) : chart === "line" ? (
                    <LineChart data={rows}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="key" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Line type="monotone" dataKey="value" stroke={COLORS[0]} strokeWidth={2} />
                    </LineChart>
                ) : (
                    <PieChart>
                        <Pie data={rows} dataKey="value" nameKey="key" outerRadius={120} label={(e) => `${e.key} (${e.value})`}>
                            {rows.map((row, i) => <Cell key={row.key} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Legend />
                        <Tooltip />
                    </PieChart>
                )}
            </ResponsiveContainer>
        </div>
    );
}

// ---- Multi-form render ----
function MultiRender({ data, variant, onDrill }) {
    const rows = data?.rows || [];
    const dims = variant.multi_group_by || [];
    const metrics = variant.multi_metrics || [{ op: "count" }];
    const metricLabels = data?.metric_labels || metrics.map((m, i) => m.label || `m${i}`);
    const chart = variant.chart_type || "bar";

    // Transform rows for recharts — flatten dim labels
    const flat = useMemo(() => rows.map((r) => {
        const out = { ...r };
        out._label = dims.length >= 2
            ? `${r.dim_0 ?? "(empty)"} · ${r.dim_1 ?? "(empty)"}`
            : String(r.dim_0 ?? "(empty)");
        return out;
    }), [rows, dims]);

    if (chart === "kpi") {
        return (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="report-run-kpi">
                {metrics.map((m, i) => {
                    const total = flat.reduce((acc, r) => acc + (Number(r[`metric_${i}`]) || 0), 0);
                    return (
                        <div key={`${m.op}-${m.field || "count"}-${i}`} className="brut-section p-5">
                            <div className="label-overline">{metricLabels[i]}</div>
                            <div className="text-3xl font-display font-bold mt-2 tabular-nums">{total.toLocaleString()}</div>
                        </div>
                    );
                })}
            </div>
        );
    }

    if (chart === "pivot") {
        // Build column set from dim_1 unique values; rows = dim_0
        const dim1Vals = Array.from(new Set(rows.map((r) => r.dim_1 ?? "(empty)")));
        const dim0Vals = Array.from(new Set(rows.map((r) => r.dim_0 ?? "(empty)")));
        const cell = (d0, d1) => rows.find((r) => (r.dim_0 ?? "(empty)") === d0 && (r.dim_1 ?? "(empty)") === d1);
        return (
            <div className="overflow-x-auto" data-testid="report-run-pivot">
                <table className="w-full text-sm border border-border">
                    <thead><tr className="bg-secondary/40">
                        <th className="px-3 py-2 text-left font-mono text-[10px] uppercase">{dims[0]?.field || ""}</th>
                        {dim1Vals.map((d) => <th key={d} className="px-3 py-2 text-left font-mono text-[10px] uppercase">{d}</th>)}
                    </tr></thead>
                    <tbody>
                        {dim0Vals.map((d0) => (
                            <tr key={d0} className="border-t border-border">
                                <td className="px-3 py-2 font-medium">{d0}</td>
                                {dim1Vals.map((d1) => {
                                    const c = cell(d0, d1);
                                    return (
                                        <td key={d1} className="px-3 py-2 font-mono cursor-pointer hover:bg-secondary/50" onClick={() => c && onDrill(c, `${d0} × ${d1}`)} data-testid={`pivot-cell-${d0}-${d1}`}>
                                            {c ? Number(c.metric_0 || 0).toLocaleString() : "—"}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
                <p className="text-[10px] text-muted-foreground mt-2 font-mono">Click any cell to see the contributing records.</p>
            </div>
        );
    }

    // bar / line / pie — use metric_0 as primary series; render extra metrics as additional bars/lines
    return (
        <div data-testid="report-run-multi-chart">
            <ResponsiveContainer width="100%" height={360}>
                {chart === "line" ? (
                    <LineChart data={flat}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="_label" tick={{ fontSize: 11 }} interval={0} angle={-25} textAnchor="end" height={80} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend />
                        {metrics.map((m, i) => <Line key={`line-${m.op}-${m.field || "count"}-${i}`} type="monotone" dataKey={`metric_${i}`} name={metricLabels[i]} stroke={COLORS[i % COLORS.length]} strokeWidth={2} />)}
                    </LineChart>
                ) : chart === "pie" ? (
                    <PieChart>
                        <Pie data={flat} dataKey="metric_0" nameKey="_label" outerRadius={120} label={(e) => `${e._label}`}>
                            {flat.map((row, i) => <Cell key={row._label} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Legend />
                        <Tooltip />
                    </PieChart>
                ) : (
                    <BarChart data={flat} onClick={(e) => {
                        if (!e || !e.activePayload || !e.activePayload[0]) return;
                        const entry = e.activePayload[0].payload;
                        onDrill(entry, entry._label);
                    }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="_label" tick={{ fontSize: 11 }} interval={0} angle={-25} textAnchor="end" height={80} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend />
                        {metrics.map((m, i) => <Bar key={`bar-${m.op}-${m.field || "count"}-${i}`} dataKey={`metric_${i}`} name={metricLabels[i]} fill={COLORS[i % COLORS.length]} cursor="pointer" />)}
                    </BarChart>
                )}
            </ResponsiveContainer>
            <p className="text-[10px] text-muted-foreground mt-2 font-mono">Click a bar to drill into the records.</p>
        </div>
    );
}

function DrillView({ rows, label, onBack, variant }) {
    return (
        <div data-testid="report-run-drill">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="label-overline">Drill-down</div>
                    <div className="text-sm font-medium mt-1">{label}</div>
                </div>
                <Button variant="ghost" size="sm" className="rounded-sm" onClick={onBack} data-testid="drill-back-btn"><ArrowLeft size={12} className="mr-1" /> Back to chart</Button>
            </div>
            <div className="border border-border rounded-sm max-h-[60vh] overflow-y-auto">
                <table className="w-full text-xs">
                    <thead><tr className="bg-secondary/40 sticky top-0">
                        <th className="px-3 py-2 text-left">Record ID</th>
                        <th className="px-3 py-2 text-left">Primary data</th>
                        {(variant.joins || []).map((j) => <th key={j.alias} className="px-3 py-2 text-left">{j.alias}</th>)}
                    </tr></thead>
                    <tbody>
                        {rows.length === 0 && <tr><td colSpan={3} className="px-3 py-6 text-center text-muted-foreground">No matching records</td></tr>}
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-border">
                                <td className="px-3 py-2 font-mono text-[10px]">{String(r.id || "").slice(0, 12)}…</td>
                                <td className="px-3 py-2 font-mono text-[10px] max-w-md truncate">{JSON.stringify(r.data || {}).slice(0, 200)}</td>
                                {(variant.joins || []).map((j) => (
                                    <td key={j.alias} className="px-3 py-2 font-mono text-[10px] max-w-xs truncate">{r[j.alias] ? JSON.stringify(r[j.alias].data || {}).slice(0, 120) : "—"}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2 font-mono">Showing first 100 contributing records.</p>
        </div>
    );
}
